import React, { useState, useEffect } from "react";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import { Link, useNavigate } from "react-router-dom";
import { 
  Card, 
  Row, 
  Col, 
  Form, 
  Button, 
  Modal, 
  Alert, 
  Spinner,
  InputGroup,
  Accordion,
  Badge,
  ProgressBar,
  Toast,
  ToastContainer
} from "react-bootstrap";
import {
  Person,
 
  GeoAlt,
 
  FileText,
  Building,
  Shop,
  Briefcase,
  
  ArrowLeft,
  Save,
  Camera,

  CheckCircle,
  XCircle,
  ExclamationCircle,
  ChevronRight,
  CreditCard,
  House,
  Bank,
  FileEarmarkText,
  Geo,
  PersonBadge
} from "react-bootstrap-icons";

// Fonction pour générer un matricule unique
const generateMatricule = () => {
  const prefix = "TXC";
  const randomNum = Math.floor(Math.random() * 900) + 100;
  const year = new Date().getFullYear().toString().slice(-2);
  return `${prefix}${year}${randomNum}`;
};

export default function NouveauContribuable() {
  const navigate = useNavigate();
  
  // États pour les données du formulaire
  const [formData, setFormData] = useState({
    // Informations de base
    matricule: generateMatricule(),
    type: "particulier",
    nom: "",
    prenom: "",
    dateNaissance: "",
    lieuNaissance: "",
    genre: "",
    situationMatrimoniale: "",
    
    // Coordonnées
    telephone: "",
    telephoneSecondaire: "",
    email: "",
    adresse: "",
    commune: "",
    departement: "",
    region: "",
    localisationGPS: "",
    
    // Informations professionnelles
    profession: "",
    activite: "",
    secteurActivite: "",
    nomEntreprise: "",
    registreCommerce: "",
    numeroNinea: "",
    dateDebutActivite: "",
    
    // Informations fiscales
    centreFiscal: "",
    regimeFiscal: "",
    activitePrincipale: "",
    activitesSecondaires: [],
    
    // Pièces jointes
    pieceIdentite: null,
    photo: null,
    justificatifDomicile: null,
    registreCommerceFile: null,
    autresDocuments: []
  });

  // États pour l'UI
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(1);
  const [notifications, setNotifications] = useState([]);
  const [alertMessage, setAlertMessage] = useState({ show: false, message: '', type: 'info' });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Options pour les selects
  const typesContribuable = [
    { value: "particulier", label: "Particulier", icon: <Person className="me-2" /> },
    { value: "entreprise", label: "Entreprise", icon: <Building className="me-2" /> },
    { value: "commercant", label: "Commerçant", icon: <Shop className="me-2" /> },
    { value: "professionnel", label: "Profession libérale", icon: <Briefcase className="me-2" /> }
  ];

  const genres = [
    { value: "masculin", label: "Masculin" },
    { value: "feminin", label: "Féminin" }
  ];

  const situationsMatrimoniales = [
    { value: "celibataire", label: "Célibataire" },
    { value: "marie", label: "Marié(e)" },
    { value: "divorce", label: "Divorcé(e)" },
    { value: "veuf", label: "Veuf/Veuve" }
  ];

  const secteursActivite = [
    { value: "agriculture", label: "Agriculture" },
    { value: "commerce", label: "Commerce" },
    { value: "industrie", label: "Industrie" },
    { value: "services", label: "Services" },
    { value: "batiment", label: "Bâtiment/Travaux Publics" },
    { value: "transport", label: "Transport" },
    { value: "tourisme", label: "Tourisme/Hôtellerie" },
    { value: "sante", label: "Santé" },
    { value: "education", label: "Éducation" },
    { value: "autres", label: "Autres" }
  ];

  const regimesFiscaux = [
    { value: "reel", label: "Régime Réel" },
    { value: "simplifie", label: "Régime Simplifié" },
    { value: "liberatoire", label: "Régime Libératoire" },
    { value: "forfaitaire", label: "Régime Forfaitaire" }
  ];

  // Fonctions pour les notifications
  const showNotification = (message, type = 'info', duration = 5000) => {
    const id = Date.now();
    const notification = {
      id,
      message,
      type,
      show: true
    };
    
    setNotifications(prev => [notification, ...prev]);
    
    setTimeout(() => {
      hideNotification(id);
    }, duration);
  };

  const hideNotification = (id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  };

  const showAlert = (message, type = 'warning', duration = 5000) => {
    setAlertMessage({ show: true, message, type });
    
    if (duration > 0) {
      setTimeout(() => {
        hideAlert();
      }, duration);
    }
  };

  const hideAlert = () => {
    setAlertMessage({ show: false, message: '', type: 'info' });
  };

  // Gestion des changements de formulaire
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Effacer l'erreur pour ce champ
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Gestion de la localisation GPS
  const handleGetLocation = () => {
    if (navigator.geolocation) {
      setLoading(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const location = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
          handleInputChange('localisationGPS', location);
          showNotification('Localisation GPS capturée avec succès', 'success');
          setLoading(false);
        },
        (error) => {
        
          showAlert('Impossible de capturer la localisation GPS', 'warning');
          setLoading(false);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } else {
      showAlert('La géolocalisation n\'est pas supportée par votre navigateur', 'warning');
    }
  };

  // Validation du formulaire
  const validateForm = () => {
    const errors = {};

    // Validation des champs obligatoires
    if (!formData.nom.trim()) errors.nom = "Le nom est obligatoire";
    if (formData.type === "particulier" && !formData.prenom.trim()) errors.prenom = "Le prénom est obligatoire";
    if (!formData.telephone.trim()) errors.telephone = "Le téléphone est obligatoire";
    if (!formData.adresse.trim()) errors.adresse = "L'adresse est obligatoire";
    if (!formData.profession.trim()) errors.profession = "La profession est obligatoire";
    if (!formData.activite.trim()) errors.activite = "L'activité est obligatoire";

    // Validation email
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Email invalide";
    }

    // Validation téléphone
    const phoneRegex = /^(\+221|221)?[0-9]{9}$/;
    const cleanPhone = formData.telephone.replace(/\s/g, '');
    if (!phoneRegex.test(cleanPhone)) {
      errors.telephone = "Numéro de téléphone invalide";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showAlert("Veuillez corriger les erreurs dans le formulaire", "warning");
      return;
    }

    setSubmitLoading(true);
    setProgress(0);

    // Simulation d'un upload progressif
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return prev;
        }
        return prev + 10;
      });
    }, 200);

    try {
      // Simulation d'un appel API
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      clearInterval(progressInterval);
      setProgress(100);

      // Ici, vous enverriez les données à votre API
     

      // Simuler une réponse réussie
      showNotification('Contribuable enregistré avec succès', 'success');
      
      // Redirection après 2 secondes
      setTimeout(() => {
        navigate('/agent/contribuables');
      }, 2000);

    } catch (error) {
      clearInterval(progressInterval);
      showAlert("Erreur lors de l'enregistrement: " + error.message, "danger");
    } finally {
      setSubmitLoading(false);
    }
  };

  // Gestion des fichiers
  const handleFileUpload = (field, files) => {
    if (files && files.length > 0) {
      handleInputChange(field, files[0]);
      showNotification(`Fichier ${field} téléchargé`, 'success');
    }
  };

  // Gestion des étapes
  const steps = [
    { number: 1, title: "Informations de base", icon: <Person /> },
    { number: 2, title: "Coordonnées", icon: <GeoAlt /> },
    { number: 3, title: "Informations professionnelles", icon: <Briefcase /> },
    { number: 4, title: "Informations fiscales", icon: <CreditCard /> },
    { number: 5, title: "Pièces jointes", icon: <FileText /> }
  ];

  const nextStep = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Calcul du pourcentage de complétion
  const completionPercentage = () => {
    const requiredFields = [
      'nom', 'telephone', 'adresse', 'profession', 'activite'
    ];
    
    let filledFields = 0;
    requiredFields.forEach(field => {
      if (formData[field] && formData[field].toString().trim() !== '') {
        filledFields++;
      }
    });

    return Math.round((filledFields / requiredFields.length) * 100);
  };

  // Composant pour les en-têtes de section
  const SectionHeader = ({ icon, title, description, step }) => (
    <div className="d-flex align-items-center mb-4">
      <div className="me-3">
        <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" 
             style={{ width: 50, height: 50 }}>
          {icon}
        </div>
      </div>
      <div>
        <div className="d-flex align-items-center">
          <h5 className="mb-0 me-2">{title}</h5>
          {step && (
            <Badge bg="primary" className="ms-2">Étape {step}</Badge>
          )}
        </div>
        <p className="text-muted mb-0">{description}</p>
      </div>
    </div>
  );

  return (
    <React.Fragment>
      <Header />
      
      <div className="main main-app p-3 p-lg-4">
        {/* Container pour les notifications Toast */}
        <ToastContainer 
          position="top-end"
          className="p-3"
          style={{ zIndex: 9999 }}
        >
          {notifications.map((notification) => (
            <Toast
              key={notification.id}
              show={notification.show}
              onClose={() => hideNotification(notification.id)}
              delay={8000}
              autohide
              className={`bg-${notification.type === 'success' ? 'success' : 
                         notification.type === 'error' ? 'danger' :
                         notification.type === 'warning' ? 'warning' : 'info'} text-white`}
            >
              <Toast.Header className={`bg-${notification.type === 'success' ? 'success' : 
                         notification.type === 'error' ? 'danger' :
                         notification.type === 'warning' ? 'warning' : 'info'} text-white`}>
                {notification.type === 'success' ? <CheckCircle className="me-2" /> :
                 notification.type === 'error' ? <XCircle className="me-2" /> :
                 notification.type === 'warning' ? <ExclamationCircle className="me-2" /> :
                 <CheckCircle className="me-2" />}
                <strong className="me-auto">
                  {notification.type === 'success' ? 'Succès' : 
                   notification.type === 'error' ? 'Erreur' :
                   notification.type === 'warning' ? 'Attention' : 'Information'}
                </strong>
                <small>{new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</small>
              </Toast.Header>
              <Toast.Body className="text-white">
                {notification.message}
              </Toast.Body>
            </Toast>
          ))}
        </ToastContainer>

        {/* Alerte Bootstrap */}
        {alertMessage.show && (
          <div className="container-fluid mb-3">
            <Alert 
              variant={alertMessage.type} 
              dismissible 
              onClose={hideAlert}
              className="shadow-sm border-0"
            >
              <div className="d-flex align-items-center">
                {alertMessage.type === 'success' ? <CheckCircle className="me-2 fs-5" /> :
                 alertMessage.type === 'danger' ? <XCircle className="me-2 fs-5" /> :
                 alertMessage.type === 'warning' ? <ExclamationCircle className="me-2 fs-5" /> :
                 <CheckCircle className="me-2 fs-5" />}
                <span className="fw-medium">{alertMessage.message}</span>
              </div>
            </Alert>
          </div>
        )}

        <div className="container-fluid">
          {/* En-tête de la page */}
          <Row className="mb-4">
            <Col md={12}>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <ol className="breadcrumb fs-sm mb-1">
                    <li className="breadcrumb-item"><Link to="/agent/dashboard">Tableau de bord</Link></li>
                    <li className="breadcrumb-item"><Link to="/agent/contribuables">Contribuables</Link></li>
                    <li className="breadcrumb-item active" aria-current="page">Nouveau contribuable</li>
                  </ol>
                  <div className="d-flex align-items-center">
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      onClick={() => navigate('/agent/contribuables/liste')}
                      className="me-3"
                    >
                      <ArrowLeft className="me-2" />
                      Retour
                    </Button>
                    <div>
                      <h4 className="main-title mb-0">Enregistrement d'un nouveau contribuable</h4>
                      <p className="text-muted mb-0">Remplissez le formulaire pour enregistrer un nouveau contribuable</p>
                    </div>
                  </div>
                </div>
                
                <div className="text-end">
                  <Badge bg="info" className="fs-sm p-2">
                    <PersonBadge className="me-2" />
                    Matricule: {formData.matricule}
                  </Badge>
                  <div className="mt-2">
                    <small className="text-muted">Complétion: {completionPercentage()}%</small>
                    <ProgressBar 
                      now={completionPercentage()} 
                      variant="info" 
                      className="mt-1" 
                      style={{ height: '4px', width: '150px' }}
                    />
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          {/* Barre de progression des étapes */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="p-3">
                  <div className="d-flex justify-content-between align-items-center">
                    {steps.map((step, index) => (
                      <React.Fragment key={step.number}>
                        <div className="d-flex flex-column align-items-center">
                          <div className={`rounded-circle d-flex align-items-center justify-content-center mb-2 
                            ${currentStep === step.number ? 'bg-primary text-white' : 
                              currentStep > step.number ? 'bg-success text-white' : 'bg-light text-muted'}`}
                            style={{ width: 40, height: 40 }}>
                            {step.icon}
                          </div>
                          <small className={`fw-medium ${currentStep >= step.number ? 'text-primary' : 'text-muted'}`}>
                            {step.title}
                          </small>
                        </div>
                        {index < steps.length - 1 && (
                          <div className="flex-grow-1 mx-2">
                            <div className={`progress ${currentStep > step.number ? 'bg-success' : 'bg-light'}`} 
                                 style={{ height: '2px' }}>
                              <div className={`progress-bar ${currentStep > step.number ? 'bg-success' : 'bg-light'}`} 
                                   style={{ width: '100%' }} />
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Formulaire principal */}
          <Row>
            <Col md={12}>
              <Form onSubmit={handleSubmit}>
                <Card className="border-0 shadow-sm mb-4">
                  <Card.Body>
                    {/* Étape 1: Informations de base */}
                    {currentStep === 1 && (
                      <>
                        <SectionHeader
                          icon={<Person />}
                          title="Informations de base"
                          description="Informations personnelles du contribuable"
                          step="1"
                        />
                        
                        <Row>
                          <Col md={4} className="mb-3">
                            <Form.Group>
                              <Form.Label>Type de contribuable <span className="text-danger">*</span></Form.Label>
                              <div className="d-grid gap-2">
                                {typesContribuable.map(type => (
                                  <Button
                                    key={type.value}
                                    type="button"
                                    variant={formData.type === type.value ? "primary" : "outline-primary"}
                                    className="d-flex align-items-center justify-content-start"
                                    onClick={() => handleInputChange('type', type.value)}
                                  >
                                    {type.icon}
                                    {type.label}
                                  </Button>
                                ))}
                              </div>
                            </Form.Group>
                          </Col>
                          
                          <Col md={8}>
                            <Row>
                              <Col md={6} className="mb-3">
                                <Form.Group>
                                  <Form.Label>Nom <span className="text-danger">*</span></Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={formData.nom}
                                    onChange={(e) => handleInputChange('nom', e.target.value)}
                                    placeholder="Entrez le nom de famille"
                                    isInvalid={!!formErrors.nom}
                                    required
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    {formErrors.nom}
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </Col>
                              
                              {formData.type === "particulier" && (
                                <Col md={6} className="mb-3">
                                  <Form.Group>
                                    <Form.Label>Prénom(s) <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                      type="text"
                                      value={formData.prenom}
                                      onChange={(e) => handleInputChange('prenom', e.target.value)}
                                      placeholder="Entrez le(s) prénom(s)"
                                      isInvalid={!!formErrors.prenom}
                                      required
                                    />
                                    <Form.Control.Feedback type="invalid">
                                      {formErrors.prenom}
                                    </Form.Control.Feedback>
                                  </Form.Group>
                                </Col>
                              )}
                              
                              {formData.type === "entreprise" && (
                                <Col md={6} className="mb-3">
                                  <Form.Group>
                                    <Form.Label>Nom de l'entreprise <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                      type="text"
                                      value={formData.nomEntreprise}
                                      onChange={(e) => handleInputChange('nomEntreprise', e.target.value)}
                                      placeholder="Nom de l'entreprise"
                                    />
                                  </Form.Group>
                                </Col>
                              )}
                            </Row>
                            
                            <Row>
                              <Col md={6} className="mb-3">
                                <Form.Group>
                                  <Form.Label>Date de naissance</Form.Label>
                                  <Form.Control
                                    type="date"
                                    value={formData.dateNaissance}
                                    onChange={(e) => handleInputChange('dateNaissance', e.target.value)}
                                  />
                                </Form.Group>
                              </Col>
                              
                              <Col md={6} className="mb-3">
                                <Form.Group>
                                  <Form.Label>Lieu de naissance</Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={formData.lieuNaissance}
                                    onChange={(e) => handleInputChange('lieuNaissance', e.target.value)}
                                    placeholder="Ville, Pays"
                                  />
                                </Form.Group>
                              </Col>
                            </Row>
                            
                            <Row>
                              <Col md={6} className="mb-3">
                                <Form.Group>
                                  <Form.Label>Genre</Form.Label>
                                  <Form.Select
                                    value={formData.genre}
                                    onChange={(e) => handleInputChange('genre', e.target.value)}
                                  >
                                    <option value="">Sélectionnez...</option>
                                    {genres.map(genre => (
                                      <option key={genre.value} value={genre.value}>{genre.label}</option>
                                    ))}
                                  </Form.Select>
                                </Form.Group>
                              </Col>
                              
                              <Col md={6} className="mb-3">
                                <Form.Group>
                                  <Form.Label>Situation matrimoniale</Form.Label>
                                  <Form.Select
                                    value={formData.situationMatrimoniale}
                                    onChange={(e) => handleInputChange('situationMatrimoniale', e.target.value)}
                                  >
                                    <option value="">Sélectionnez...</option>
                                    {situationsMatrimoniales.map(situation => (
                                      <option key={situation.value} value={situation.value}>{situation.label}</option>
                                    ))}
                                  </Form.Select>
                                </Form.Group>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </>
                    )}

                    {/* Étape 2: Coordonnées */}
                    {currentStep === 2 && (
                      <>
                        <SectionHeader
                          icon={<GeoAlt />}
                          title="Coordonnées"
                          description="Informations de contact et localisation"
                          step="2"
                        />
                        
                        <Row>
                          <Col md={6} className="mb-3">
                            <Form.Group>
                              <Form.Label>Téléphone principal <span className="text-danger">*</span></Form.Label>
                              <InputGroup>
                                <InputGroup.Text>+221</InputGroup.Text>
                                <Form.Control
                                  type="tel"
                                  value={formData.telephone}
                                  onChange={(e) => handleInputChange('telephone', e.target.value)}
                                  placeholder="77 123 4567"
                                  isInvalid={!!formErrors.telephone}
                                  required
                                />
                              </InputGroup>
                              <Form.Control.Feedback type="invalid">
                                {formErrors.telephone}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                          
                          <Col md={6} className="mb-3">
                            <Form.Group>
                              <Form.Label>Téléphone secondaire</Form.Label>
                              <InputGroup>
                                <InputGroup.Text>+221</InputGroup.Text>
                                <Form.Control
                                  type="tel"
                                  value={formData.telephoneSecondaire}
                                  onChange={(e) => handleInputChange('telephoneSecondaire', e.target.value)}
                                  placeholder="76 987 6543"
                                />
                              </InputGroup>
                            </Form.Group>
                          </Col>
                        </Row>
                        
                        <Row>
                          <Col md={6} className="mb-3">
                            <Form.Group>
                              <Form.Label>Email</Form.Label>
                              <Form.Control
                                type="email"
                                value={formData.email}
                                onChange={(e) => handleInputChange('email', e.target.value)}
                                placeholder="exemple@email.com"
                                isInvalid={!!formErrors.email}
                              />
                              <Form.Control.Feedback type="invalid">
                                {formErrors.email}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                          
                          <Col md={6} className="mb-3">
                            <Form.Group>
                              <Form.Label>Adresse <span className="text-danger">*</span></Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.adresse}
                                onChange={(e) => handleInputChange('adresse', e.target.value)}
                                placeholder="Rue, Numéro, Quartier"
                                isInvalid={!!formErrors.adresse}
                                required
                              />
                              <Form.Control.Feedback type="invalid">
                                {formErrors.adresse}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>
                        
                        <Row>
                          <Col md={4} className="mb-3">
                            <Form.Group>
                              <Form.Label>Commune</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.commune}
                                onChange={(e) => handleInputChange('commune', e.target.value)}
                                placeholder="Commune"
                              />
                            </Form.Group>
                          </Col>
                          
                          <Col md={4} className="mb-3">
                            <Form.Group>
                              <Form.Label>Département</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.departement}
                                onChange={(e) => handleInputChange('departement', e.target.value)}
                                placeholder="Département"
                              />
                            </Form.Group>
                          </Col>
                          
                          <Col md={4} className="mb-3">
                            <Form.Group>
                              <Form.Label>Région</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.region}
                                onChange={(e) => handleInputChange('region', e.target.value)}
                                placeholder="Région"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        
                        <Row>
                          <Col md={12} className="mb-3">
                            <Form.Group>
                              <Form.Label>Localisation GPS</Form.Label>
                              <InputGroup>
                                <Form.Control
                                  type="text"
                                  value={formData.localisationGPS}
                                  onChange={(e) => handleInputChange('localisationGPS', e.target.value)}
                                  placeholder="Coordonnées GPS"
                                  readOnly
                                />
                                <Button 
                                  variant="outline-primary"
                                  onClick={handleGetLocation}
                                  disabled={loading}
                                >
                                  {loading ? (
                                    <Spinner animation="border" size="sm" className="me-2" />
                                  ) : (
                                    <Geo className="me-2" />
                                  )}
                                  {loading ? 'Capture...' : 'Capturer GPS'}
                                </Button>
                              </InputGroup>
                              <Form.Text className="text-muted">
                                Cliquez sur "Capturer GPS" pour obtenir automatiquement les coordonnées
                              </Form.Text>
                            </Form.Group>
                          </Col>
                        </Row>
                      </>
                    )}

                    {/* Étape 3: Informations professionnelles */}
                    {currentStep === 3 && (
                      <>
                        <SectionHeader
                          icon={<Briefcase />}
                          title="Informations professionnelles"
                          description="Activité et profession du contribuable"
                          step="3"
                        />
                        
                        <Row>
                          <Col md={6} className="mb-3">
                            <Form.Group>
                              <Form.Label>Profession <span className="text-danger">*</span></Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.profession}
                                onChange={(e) => handleInputChange('profession', e.target.value)}
                                placeholder="Ex: Commerçant, Médecin, Entrepreneur"
                                isInvalid={!!formErrors.profession}
                                required
                              />
                              <Form.Control.Feedback type="invalid">
                                {formErrors.profession}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                          
                          <Col md={6} className="mb-3">
                            <Form.Group>
                              <Form.Label>Activité principale <span className="text-danger">*</span></Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.activite}
                                onChange={(e) => handleInputChange('activite', e.target.value)}
                                placeholder="Ex: Vente de produits alimentaires"
                                isInvalid={!!formErrors.activite}
                                required
                              />
                              <Form.Control.Feedback type="invalid">
                                {formErrors.activite}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>
                        
                        <Row>
                          <Col md={6} className="mb-3">
                            <Form.Group>
                              <Form.Label>Secteur d'activité</Form.Label>
                              <Form.Select
                                value={formData.secteurActivite}
                                onChange={(e) => handleInputChange('secteurActivite', e.target.value)}
                              >
                                <option value="">Sélectionnez un secteur</option>
                                {secteursActivite.map(secteur => (
                                  <option key={secteur.value} value={secteur.value}>{secteur.label}</option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          
                          <Col md={6} className="mb-3">
                            <Form.Group>
                              <Form.Label>Date de début d'activité</Form.Label>
                              <Form.Control
                                type="date"
                                value={formData.dateDebutActivite}
                                onChange={(e) => handleInputChange('dateDebutActivite', e.target.value)}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        
                        {formData.type === "entreprise" && (
                          <>
                            <h6 className="mt-4 mb-3 text-primary">
                              <Building className="me-2" />
                              Informations de l'entreprise
                            </h6>
                            <Row>
                              <Col md={6} className="mb-3">
                                <Form.Group>
                                  <Form.Label>Registre de commerce</Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={formData.registreCommerce}
                                    onChange={(e) => handleInputChange('registreCommerce', e.target.value)}
                                    placeholder="Numéro de registre"
                                  />
                                </Form.Group>
                              </Col>
                              
                              <Col md={6} className="mb-3">
                                <Form.Group>
                                  <Form.Label>Numéro NINEA</Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={formData.numeroNinea}
                                    onChange={(e) => handleInputChange('numeroNinea', e.target.value)}
                                    placeholder="Numéro NINEA"
                                  />
                                </Form.Group>
                              </Col>
                            </Row>
                          </>
                        )}
                      </>
                    )}

                    {/* Étape 4: Informations fiscales */}
                    {currentStep === 4 && (
                      <>
                        <SectionHeader
                          icon={<CreditCard />}
                          title="Informations fiscales"
                          description="Détails du régime fiscal et activités"
                          step="4"
                        />
                        
                        <Row>
                          <Col md={6} className="mb-3">
                            <Form.Group>
                              <Form.Label>Centre fiscal d'affectation</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.centreFiscal}
                                onChange={(e) => handleInputChange('centreFiscal', e.target.value)}
                                placeholder="Ex: Centre des Impôts de Dakar"
                              />
                            </Form.Group>
                          </Col>
                          
                          <Col md={6} className="mb-3">
                            <Form.Group>
                              <Form.Label>Régime fiscal applicable</Form.Label>
                              <Form.Select
                                value={formData.regimeFiscal}
                                onChange={(e) => handleInputChange('regimeFiscal', e.target.value)}
                              >
                                <option value="">Sélectionnez un régime</option>
                                {regimesFiscaux.map(regime => (
                                  <option key={regime.value} value={regime.value}>{regime.label}</option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>
                        
                        <Row>
                          <Col md={12} className="mb-3">
                            <Form.Group>
                              <Form.Label>Activité principale déclarée</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.activitePrincipale}
                                onChange={(e) => handleInputChange('activitePrincipale', e.target.value)}
                                placeholder="Décrivez l'activité principale"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        
                        <Row>
                          <Col md={12}>
                            <Form.Group>
                              <Form.Label>Activités secondaires (optionnel)</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                value={formData.activitesSecondaires.join(', ')}
                                onChange={(e) => handleInputChange('activitesSecondaires', e.target.value.split(',').map(a => a.trim()))}
                                placeholder="Listez les activités secondaires séparées par des virgules"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </>
                    )}

                    {/* Étape 5: Pièces jointes */}
                    {currentStep === 5 && (
                      <>
                        <SectionHeader
                          icon={<FileText />}
                          title="Pièces jointes"
                          description="Documents nécessaires pour l'enregistrement"
                          step="5"
                        />
                        
                        <Accordion defaultActiveKey="0" className="mb-4">
                          <Accordion.Item eventKey="0">
                            <Accordion.Header>
                              <FileEarmarkText className="me-2" />
                              Pièce d'identité
                            </Accordion.Header>
                            <Accordion.Body>
                              <Row>
                                <Col md={6}>
                                  <Form.Group>
                                    <Form.Label>Pièce d'identité (CNI, Passeport) <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                      type="file"
                                      accept=".jpg,.jpeg,.png,.pdf"
                                      onChange={(e) => handleFileUpload('pieceIdentite', e.target.files)}
                                    />
                                    <Form.Text className="text-muted">
                                      Formats acceptés: JPG, PNG, PDF. Taille max: 5MB
                                    </Form.Text>
                                    {formData.pieceIdentite && (
                                      <Alert variant="success" className="mt-2 py-2">
                                        <CheckCircle className="me-2" />
                                        Fichier sélectionné: {formData.pieceIdentite.name}
                                      </Alert>
                                    )}
                                  </Form.Group>
                                </Col>
                                
                                <Col md={6}>
                                  <Form.Group>
                                    <Form.Label>Photo d'identité</Form.Label>
                                    <Form.Control
                                      type="file"
                                      accept=".jpg,.jpeg,.png"
                                      onChange={(e) => handleFileUpload('photo', e.target.files)}
                                    />
                                    <Form.Text className="text-muted">
                                      Photo récente, format JPG ou PNG
                                    </Form.Text>
                                  </Form.Group>
                                </Col>
                              </Row>
                            </Accordion.Body>
                          </Accordion.Item>
                          
                          <Accordion.Item eventKey="1">
                            <Accordion.Header>
                              <House className="me-2" />
                              Justificatif de domicile
                            </Accordion.Header>
                            <Accordion.Body>
                              <Form.Group>
                                <Form.Label>Justificatif de domicile</Form.Label>
                                <Form.Control
                                  type="file"
                                  accept=".jpg,.jpeg,.png,.pdf"
                                  onChange={(e) => handleFileUpload('justificatifDomicile', e.target.files)}
                                />
                                <Form.Text className="text-muted">
                                  Facture d'eau, d'électricité ou quittance de loyer
                                </Form.Text>
                              </Form.Group>
                            </Accordion.Body>
                          </Accordion.Item>
                          
                          {formData.type === "entreprise" && (
                            <Accordion.Item eventKey="2">
                              <Accordion.Header>
                                <Bank className="me-2" />
                                Documents de l'entreprise
                              </Accordion.Header>
                              <Accordion.Body>
                                <Row>
                                  <Col md={6}>
                                    <Form.Group>
                                      <Form.Label>Registre de commerce</Form.Label>
                                      <Form.Control
                                        type="file"
                                        accept=".pdf"
                                        onChange={(e) => handleFileUpload('registreCommerceFile', e.target.files)}
                                      />
                                    </Form.Group>
                                  </Col>
                                  <Col md={6}>
                                    <Form.Group>
                                      <Form.Label>Autres documents</Form.Label>
                                      <Form.Control
                                        type="file"
                                        multiple
                                        onChange={(e) => handleInputChange('autresDocuments', Array.from(e.target.files))}
                                      />
                                      <Form.Text className="text-muted">
                                        Vous pouvez sélectionner plusieurs fichiers
                                      </Form.Text>
                                    </Form.Group>
                                  </Col>
                                </Row>
                              </Accordion.Body>
                            </Accordion.Item>
                          )}
                        </Accordion>
                        
                        {/* Aperçu des fichiers téléchargés */}
                        {(formData.pieceIdentite || formData.photo || formData.justificatifDomicile || formData.autresDocuments?.length > 0) && (
                          <Card className="border-primary mb-4">
                            <Card.Header className="bg-primary text-white">
                              <h6 className="mb-0">Aperçu des documents</h6>
                            </Card.Header>
                            <Card.Body>
                              <Row>
                                {formData.pieceIdentite && (
                                  <Col md={4} className="mb-2">
                                    <Badge bg="success" className="w-100 py-2">
                                      <FileEarmarkText className="me-2" />
                                      Pièce d'identité: ✓
                                    </Badge>
                                  </Col>
                                )}
                                {formData.photo && (
                                  <Col md={4} className="mb-2">
                                    <Badge bg="success" className="w-100 py-2">
                                      <Camera className="me-2" />
                                      Photo: ✓
                                    </Badge>
                                  </Col>
                                )}
                                {formData.justificatifDomicile && (
                                  <Col md={4} className="mb-2">
                                    <Badge bg="success" className="w-100 py-2">
                                      <House className="me-2" />
                                      Domicile: ✓
                                    </Badge>
                                  </Col>
                                )}
                                {formData.autresDocuments?.length > 0 && (
                                  <Col md={4} className="mb-2">
                                    <Badge bg="info" className="w-100 py-2">
                                      <FileText className="me-2" />
                                      {formData.autresDocuments.length} autre(s)
                                    </Badge>
                                  </Col>
                                )}
                              </Row>
                            </Card.Body>
                          </Card>
                        )}
                      </>
                    )}
                  </Card.Body>
                  
                  <Card.Footer className="bg-light d-flex justify-content-between">
                    <div>
                      {currentStep > 1 && (
                        <Button variant="outline-secondary" onClick={prevStep}>
                          <ArrowLeft className="me-2" />
                          Étape précédente
                        </Button>
                      )}
                    </div>
                    
                    <div className="d-flex gap-2">
                      {currentStep < 5 ? (
                        <Button variant="primary" onClick={nextStep}>
                          Étape suivante
                          <ChevronRight className="ms-2" />
                        </Button>
                      ) : (
                        <>
                          <Button 
                            variant="outline-secondary"
                            onClick={() => setShowConfirmModal(true)}
                          >
                            Aperçu avant enregistrement
                          </Button>
                          <Button 
                            variant="success" 
                            type="submit"
                            disabled={submitLoading}
                          >
                            {submitLoading ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Enregistrement...
                              </>
                            ) : (
                              <>
                                <Save className="me-2" />
                                Enregistrer le contribuable
                              </>
                            )}
                          </Button>
                        </>
                      )}
                    </div>
                  </Card.Footer>
                </Card>
                
                {submitLoading && (
                  <Card className="border-0 shadow-sm mb-4">
                    <Card.Body>
                      <div className="text-center">
                        <Spinner animation="border" variant="primary" className="mb-3" />
                        <h5>Enregistrement en cours...</h5>
                        <p className="text-muted">Veuillez patienter pendant l'enregistrement du contribuable</p>
                        <ProgressBar 
                          now={progress} 
                          variant="success" 
                          animated 
                          className="mb-3"
                          label={`${progress}%`}
                        />
                      </div>
                    </Card.Body>
                  </Card>
                )}
              </Form>
            </Col>
          </Row>
        </div>

        {/* Modal de confirmation */}
        <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} size="lg">
          <Modal.Header closeButton className="bg-primary text-white">
            <Modal.Title>
              <CheckCircle className="me-2" />
              Aperçu avant enregistrement
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="info" className="mb-4">
              <ExclamationCircle className="me-2" />
              Vérifiez les informations ci-dessous avant d'enregistrer définitivement le contribuable.
            </Alert>
            
            <Row>
              <Col md={6}>
                <Card className="mb-3">
                  <Card.Header className="bg-light">
                    <h6 className="mb-0">Informations personnelles</h6>
                  </Card.Header>
                  <Card.Body>
                    <p><strong>Matricule:</strong> {formData.matricule}</p>
                    <p><strong>Type:</strong> {
                      typesContribuable.find(t => t.value === formData.type)?.label || formData.type
                    }</p>
                    <p><strong>Nom:</strong> {formData.nom}</p>
                    {formData.prenom && <p><strong>Prénom:</strong> {formData.prenom}</p>}
                    {formData.genre && <p><strong>Genre:</strong> {formData.genre}</p>}
                  </Card.Body>
                </Card>
              </Col>
              
              <Col md={6}>
                <Card className="mb-3">
                  <Card.Header className="bg-light">
                    <h6 className="mb-0">Coordonnées</h6>
                  </Card.Header>
                  <Card.Body>
                    <p><strong>Téléphone:</strong> {formData.telephone}</p>
                    {formData.email && <p><strong>Email:</strong> {formData.email}</p>}
                    <p><strong>Adresse:</strong> {formData.adresse}</p>
                    {formData.localisationGPS && <p><strong>Localisation GPS:</strong> {formData.localisationGPS}</p>}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            
            <Row>
              <Col md={12}>
                <Card className="mb-3">
                  <Card.Header className="bg-light">
                    <h6 className="mb-0">Informations professionnelles</h6>
                  </Card.Header>
                  <Card.Body>
                    <p><strong>Profession:</strong> {formData.profession}</p>
                    <p><strong>Activité:</strong> {formData.activite}</p>
                    {formData.secteurActivite && <p><strong>Secteur:</strong> {
                      secteursActivite.find(s => s.value === formData.secteurActivite)?.label || formData.secteurActivite
                    }</p>}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            
            <Row>
              <Col md={12}>
                <Card>
                  <Card.Header className="bg-light">
                    <h6 className="mb-0">Documents attachés</h6>
                  </Card.Header>
                  <Card.Body>
                    <div className="d-flex flex-wrap gap-2">
                      {formData.pieceIdentite && (
                        <Badge bg="success" className="p-2">
                          <FileEarmarkText className="me-2" />
                          Pièce d'identité
                        </Badge>
                      )}
                      {formData.photo && (
                        <Badge bg="success" className="p-2">
                          <Camera className="me-2" />
                          Photo
                        </Badge>
                      )}
                      {formData.justificatifDomicile && (
                        <Badge bg="success" className="p-2">
                          <House className="me-2" />
                          Justificatif domicile
                        </Badge>
                      )}
                      {(!formData.pieceIdentite && !formData.photo && !formData.justificatifDomicile) && (
                        <Badge bg="warning" className="p-2">
                          <ExclamationCircle className="me-2" />
                          Aucun document attaché
                        </Badge>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
              Modifier
            </Button>
            <Button 
              variant="success" 
              onClick={() => {
                setShowConfirmModal(false);
                handleSubmit({ preventDefault: () => {} });
              }}
              disabled={submitLoading}
            >
              <Save className="me-2" />
              Confirmer l'enregistrement
            </Button>
          </Modal.Footer>
        </Modal>
      </div>

      {/* Styles supplémentaires */}
      <style>{`
        .step-indicator {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
        }
        
        .step-complete {
          background-color: #28a745;
          color: white;
        }
        
        .step-current {
          background-color: #007bff;
          color: white;
        }
        
        .step-pending {
          background-color: #e9ecef;
          color: #6c757d;
        }
        
        .file-upload-area {
          border: 2px dashed #dee2e6;
          border-radius: 0.375rem;
          padding: 2rem;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .file-upload-area:hover {
          border-color: #007bff;
          background-color: #f8f9fa;
        }
        
        .stats-card {
          transition: transform 0.3s ease;
        }
        
        .stats-card:hover {
          transform: translateY(-5px);
        }
      `}</style>
      
      <Footer />
    </React.Fragment>
  );
}